<template>
  <section v-if="tabs.length" class="workspace-window" :class="`workspace-window--${mode}`" aria-label="Documentation">
    <header class="workspace-header">
      <span class="workspace-title">Documentation</span>
      <div class="workspace-tabs" aria-label="Opened workflows">
        <div v-for="tab in tabs" :key="tab.id" class="workspace-tab" :class="{ 'is-active': tab.id === activeId }">
          <button
            type="button"
            :aria-pressed="tab.id === activeId"
            :aria-controls="`workspace-panel-${tab.id}`"
            @click="select(tab.id)"
          >
            {{ tab.title }}
          </button>
          <button type="button" :aria-label="`Close ${tab.title}`" @click="closeTab(tab.id)">
            <a-icon width="16" height="16" icon="material-symbols:close-rounded" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div class="workspace-controls">
        <button
          v-if="mode !== 'minimized'"
          ref="minimizeControl"
          type="button"
          title="Minimize"
          aria-label="Minimize documentation"
          @click="setMode('minimized')"
        >
          <a-icon width="20" height="20" icon="material-symbols:remove" aria-hidden="true" />
        </button>
        <button
          type="button"
          :title="mode === 'fullscreen' ? 'Window view' : 'Full view'"
          :aria-label="mode === 'fullscreen' ? 'Use window view' : 'Use full view'"
          @click="setMode(mode === 'fullscreen' ? 'window' : 'fullscreen')"
        >
          <a-icon
            width="20"
            height="20"
            :icon="
              mode === 'fullscreen'
                ? 'material-symbols:close-fullscreen-rounded'
                : 'material-symbols:fullscreen-rounded'
            "
            aria-hidden="true"
          />
        </button>
        <button
          v-if="mode === 'minimized'"
          ref="restoreControl"
          type="button"
          title="Restore"
          aria-label="Restore documentation"
          @click="setMode(restoreMode)"
        >
          <a-icon width="20" height="20" icon="material-symbols:close-fullscreen-rounded" aria-hidden="true" />
        </button>
        <button type="button" title="Close documentation" aria-label="Close documentation" @click="closeDocumentation">
          <a-icon width="20" height="20" icon="material-symbols:close-rounded" aria-hidden="true" />
        </button>
      </div>
    </header>
    <div v-show="mode !== 'minimized'" class="workspace-body">
      <aside class="workspace-explorer" aria-label="Workflows">
        <button
          v-for="entry in catalog"
          :id="`workspace-entry-${entry.id}`"
          :key="entry.id"
          type="button"
          :class="{ 'is-active': entry.id === activeId }"
          :aria-pressed="entry.id === activeId"
          :aria-controls="`workspace-panel-${entry.id}`"
          @click="open(entry)"
        >
          {{ entry.title }}
        </button>
      </aside>
      <div class="workspace-content">
        <div
          v-for="tab in tabs"
          v-show="tab.id === activeId"
          :id="`workspace-panel-${tab.id}`"
          :key="tab.id"
          class="workspace-panel"
          role="region"
          :aria-label="tab.title"
        >
          <slot :tab="tab" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { documentationWorkflows } from '../visualization/workflows'

export default {
  name: 'WorkspaceWindow',
  data() {
    return { catalog: documentationWorkflows }
  },
  computed: { ...mapState('workspace', ['tabs', 'activeId', 'mode', 'restoreMode']) },
  watch: {
    activeId: 'updateContentSize',
    mode(mode, previous) {
      this.updateContentSize()
      if (mode === 'minimized') this.$nextTick(() => this.$refs.restoreControl?.focus())
      else if (previous === 'minimized') this.$nextTick(() => this.$refs.minimizeControl?.focus())
    },
    'tabs.length'(length, previous) {
      if (length && !previous) {
        this.returnFocus = document.activeElement
        this.$nextTick(() => this.$el.querySelector('.workspace-explorer .is-active')?.focus())
      } else if (!length && previous) {
        this.restoreFocus()
      }
    }
  },
  methods: {
    ...mapMutations('workspace', ['open', 'select', 'close', 'setMode', 'clear']),
    closeDocumentation() {
      this.clear()
    },
    closeTab(id) {
      this.close(id)
      if (this.tabs.length) {
        this.$nextTick(() => {
          if (this.mode === 'minimized') this.$refs.restoreControl?.focus()
          else this.$el.querySelector('.workspace-explorer .is-active')?.focus()
        })
      }
    },
    restoreFocus() {
      if (this.returnFocus?.isConnected) this.returnFocus.focus()
      this.returnFocus = null
    },
    updateContentSize() {
      this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    }
  }
}
</script>

<style scoped>
.workspace-window {
  position: fixed;
  inset: 88px 24px 24px;
  z-index: 900;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border-color-base);
  border-radius: 8px;
  background: var(--bg-component);
  color: var(--text-color);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
}
.workspace-window--fullscreen {
  inset: 0;
  border-radius: 0;
}
.workspace-window--minimized {
  inset: auto 24px 12px;
}
.workspace-header {
  display: flex;
  align-items: center;
  min-height: 44px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-color-base);
  background: var(--bg-body);
}
.workspace-title {
  padding: 0 16px;
  font-size: 12px;
  color: var(--text-color-alpha6);
}
.workspace-tabs {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  align-self: stretch;
}
.workspace-tab {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  border-right: 1px solid var(--border-color-base);
  border-top: 2px solid transparent;
}
.workspace-tab.is-active {
  border-top-color: var(--primary-color);
  background: var(--bg-component);
}
button {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
}
.workspace-tab button {
  min-height: 40px;
  padding: 0 12px;
  white-space: nowrap;
}
.workspace-tab button + button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  color: var(--text-color-alpha6);
}
.workspace-controls {
  display: flex;
  flex-shrink: 0;
  padding: 0 4px;
}
.workspace-controls button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}
button:hover {
  background: var(--text-color-alpha2);
}
button:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: -2px;
}
.workspace-body {
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
}
.workspace-explorer {
  width: 170px;
  flex-shrink: 0;
  overflow-y: auto;
  border-right: 1px solid var(--border-color-base);
  background: var(--bg-body);
}
.workspace-explorer button {
  display: block;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
}
.workspace-explorer button.is-active {
  color: var(--primary-color);
}
.workspace-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: auto;
}
.workspace-panel {
  height: 100%;
  min-width: 0;
}
@media (max-width: 600px) {
  .workspace-window {
    inset: 64px 0 0;
    border-radius: 0;
  }
  .workspace-window--fullscreen {
    inset: 0;
  }
  .workspace-window--minimized {
    inset: auto 0 0;
  }
  .workspace-header {
    flex-wrap: wrap;
  }
  .workspace-title {
    flex: 1;
  }
  .workspace-controls {
    order: 1;
  }
  .workspace-tabs {
    order: 2;
    flex-basis: 100%;
  }
  .workspace-body {
    flex-direction: column;
  }
  .workspace-explorer {
    display: flex;
    width: 100%;
    overflow-x: auto;
    border-right: 0;
    border-bottom: 1px solid var(--border-color-base);
  }
  .workspace-explorer button {
    width: auto;
    flex: 1;
    white-space: nowrap;
  }
}
</style>
